import React, { useState, useEffect, useCallback } from 'react';
import {
    initializeMusicKit,
    authorize as authorizeApple,
    isAuthorized as isAuthorizedApple,
    unauthorize as unauthorizeApple,
    addToLibraryBatched,
} from './services/musicKit.js';
import {
    initializeSpotify,
    authorize as authorizeSpotify,
    isAuthorized as isAuthorizedSpotify,
    unauthorize as unauthorizeSpotify,
    handleCallback as handleSpotifyCallback,
    saveTracksBatched,
} from './services/spotifyService.js';
import { parseInputText } from './utils/textParser.js';
import { matchAllSongs, getMatchSummary, getImportableResults } from './services/songMatcher.js';
import { matchAllSongsSpotify, getMatchSummarySpotify, getImportableResultsSpotify } from './services/spotifyMatcher.js';

const EXAMPLE_SONGS = `Bohemian Rhapsody - Queen
Hotel California - Eagles
Stairway to Heaven - Led Zeppelin
Billie Jean - Michael Jackson
Sweet Child O' Mine - Guns N' Roses`;

function App() {
    const [inputText, setInputText] = useState('');
    const [provider, setProvider] = useState('apple'); // 'apple' | 'spotify'
    const [isAuth, setIsAuth] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [includeUncertain, setIncludeUncertain] = useState(true);

    // Handle Spotify OAuth callback
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const spotifyError = urlParams.get('error');

        if (spotifyError) {
            setError(`SPOTIFY_AUTH_ERROR: ${spotifyError}`);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        if (code) {
            // Handle Spotify callback
            setProvider('spotify');
            handleSpotifyCallback(code)
                .then(() => {
                    setIsAuth(true);
                    setIsInitialized(true);
                    // Clean URL
                    window.history.replaceState({}, document.title, window.location.pathname);
                })
                .catch(err => {
                    setError(`SPOTIFY_CALLBACK_ERROR: ${err.message}`);
                    setIsInitialized(true);
                    window.history.replaceState({}, document.title, window.location.pathname);
                });
            return;
        }

        // Normal initialization
        initServices();
    }, []);

    async function initServices() {
        try {
            // Initialize both services
            const [,] = await Promise.all([
                initializeMusicKit().catch(err => {
                    console.warn('[Init] MusicKit init failed:', err.message);
                }),
                initializeSpotify().catch(err => {
                    console.warn('[Init] Spotify init failed:', err.message);
                }),
            ]);

            setIsInitialized(true);

            // Check auth status for current provider
            await checkAuthStatus();
        } catch (err) {
            setError(`INTERNAL_ERROR: ${err.message}`);
            setIsInitialized(true);
        }
    }

    async function checkAuthStatus() {
        if (provider === 'apple') {
            const authorized = await isAuthorizedApple();
            setIsAuth(authorized);
        } else {
            const authorized = isAuthorizedSpotify();
            setIsAuth(authorized);
        }
    }

    const [showGuide, setShowGuide] = useState(false);

    // ... (keep existing useEffects/callbacks unchanged)

    // Re-check auth when provider changes
    useEffect(() => {
        if (isInitialized) {
            checkAuthStatus();
            setResults(null); // Clear results when switching providers
        }
    }, [provider, isInitialized]);

    const handleAuthorize = useCallback(async () => {
        try {
            setError(null);
            if (provider === 'apple') {
                await authorizeApple();
                setIsAuth(true);
            } else {
                // Spotify redirects to their auth page
                await authorizeSpotify();
                // After redirect, the useEffect will handle the callback
            }
        } catch (err) {
            setError(`AUTH_FAILED: ${err.message}`);
        }
    }, [provider]);

    const handleSignOut = useCallback(async () => {
        try {
            if (provider === 'apple') {
                await unauthorizeApple();
            } else {
                await unauthorizeSpotify();
            }
            setIsAuth(false);
            setResults(null);
        } catch (err) {
            setError(`SIGNOUT_FAILED: ${err.message}`);
        }
    }, [provider]);

    const handleImport = useCallback(async () => {
        if (!inputText.trim()) {
            setError('INPUT_REQUIRED: PASTE TRACKLIST');
            return;
        }
        setIsLoading(true);
        setError(null);
        setResults(null);

        try {
            setProgress({ phase: '01', message: 'PARSING_DATA', completed: 0, total: 100 });
            const parsed = parseInputText(inputText);
            if (parsed.length === 0) throw new Error('DATA_FORMAT_ERROR');

            setProgress({ phase: '02', message: 'MATCHING_CATALOG', completed: 0, total: parsed.length });

            let matches, summary, toImport;

            if (provider === 'apple') {
                matches = await matchAllSongs(parsed, {
                    onProgress: (p) => setProgress({ phase: '02', message: `FINDING_SONGS_${p.completed}/${p.total}`, ...p })
                });
                toImport = getImportableResults(matches, { includeUncertain });
                summary = getMatchSummary(matches);
            } else {
                matches = await matchAllSongsSpotify(parsed, {
                    onProgress: (p) => setProgress({ phase: '02', message: `FINDING_SONGS_${p.completed}/${p.total}`, ...p })
                });
                toImport = getImportableResultsSpotify(matches, { includeUncertain });
                summary = getMatchSummarySpotify(matches);
            }

            if (toImport.length === 0) {
                setResults({ matches, summary, imported: 0, importFailed: 0 });
                setProgress(null);
                setIsLoading(false);
                return;
            }

            setProgress({ phase: '03', message: 'INJECTING_LIBRARY', completed: 0, total: toImport.length });
            const songIds = toImport.map(m => m.match.id);

            let importResult;
            if (provider === 'apple') {
                importResult = await addToLibraryBatched(songIds, {
                    onProgress: (p) => setProgress({ phase: '03', message: `PUSHING_${p.processed}/${p.total}`, completed: p.processed, total: p.total })
                });
            } else {
                importResult = await saveTracksBatched(songIds, {
                    onProgress: (p) => setProgress({ phase: '03', message: `PUSHING_${p.processed}/${p.total}`, completed: p.processed, total: p.total })
                });
            }

            setResults({ matches, summary, imported: importResult.success, importFailed: importResult.failed });
        } catch (err) {
            setError(`EXECUTION_FAILED: ${err.message}`);
        } finally {
            setIsLoading(false);
            setProgress(null);
        }
    }, [inputText, includeUncertain, provider]);

    const handleReset = useCallback(() => {
        setResults(null);
        setInputText('');
        setError(null);
    }, []);

    const handleKeyDown = (e) => {
        if (e.key === 'Tab' && !inputText.trim()) {
            e.preventDefault();
            setInputText(EXAMPLE_SONGS);
        }
    };

    const parsedCount = parseInputText(inputText).length;
    const providerLabel = provider === 'apple' ? 'APPLE MUSIC' : 'SPOTIFY';

    if (!isInitialized) return null; // Let the index.html loader show

    return (
        <div className="brutalist-root">
            <div className="coffee-stain" style={{ top: '10%', left: '70%' }}></div>
            <div className="coffee-stain" style={{ bottom: '5%', right: '80%', transform: 'scale(0.6)' }}></div>



            <div className="doodle doodle-headphones floating" style={{ top: '250px', right: '2%', animationDelay: '0.5s' }}>
                <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M20 60 Q20 20 50 20 Q80 20 80 60" strokeLinecap="round" />
                    <rect x="10" y="60" width="15" height="25" rx="5" fill="rgba(0,0,0,0.05)" />
                    <rect x="75" y="60" width="15" height="25" rx="5" fill="rgba(0,0,0,0.05)" />
                </svg>
            </div>


            <div className="doodle doodle-note floating" style={{ top: '400px', left: '2%' }}>
                <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M30 80 Q30 70 50 70 L50 20 L80 30 L80 50" />
                    <circle cx="20" cy="80" r="10" />
                    <circle cx="70" cy="60" r="10" />
                </svg>
            </div>

            <div className="doodle doodle-guitar floating" style={{ top: '650px', right: '3%', animationDelay: '1s' }}>
                <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M50 80 L50 30 M45 35 L55 35 M45 45 L55 45 M45 55 L55 55" />
                    <path d="M40 85 Q50 95 60 85 L60 75 Q50 65 40 75 Z" fill="rgba(0,0,0,0.05)" />
                    <path d="M50 30 L50 20 Q50 15 60 15" />
                </svg>
            </div>


            <div className="doodle doodle-speaker floating" style={{ bottom: '150px', left: '5%', opacity: 0.4 }}>
                <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="25" y="20" width="50" height="60" />
                    <circle cx="50" cy="40" r="10" />
                    <circle cx="50" cy="65" r="15" />
                </svg>
            </div>

            <div className="doodle doodle-cd floating" style={{ top: '350px', right: '5%', transform: 'rotate(20deg)' }}>
                <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="50" cy="50" r="40" />
                    <circle cx="50" cy="50" r="10" />
                    <path d="M50 10 A40 40 0 0 1 90 50" opacity="0.3" />
                </svg>
            </div>







            {/* ... marquee code ... */}
            <div style={{ position: 'relative' }}>
                <div className={`marquee-container ${isAuth && provider === 'apple' ? 'connected-apple' : ''}`} style={{ position: 'relative' }}>
                    <div className="marquee-content">
                        {/* Set 1 */}
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        {/* Set 2 */}
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        {/* Set 3 */}
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        {/* Set 4 */}
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                        <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                    </div>
                </div>

                {/* Fido Dido Hanging (Outside marquee-container to avoid overflow hidden) */}
                <div className="doodle-fidodio-container" style={{ position: 'absolute', top: '100%', right: '24%', marginTop: '-10px', zIndex: 20, pointerEvents: 'none' }}>
                    <img src="/fidodio.png" alt="Fido Dido" className="doodle-fidodio" />
                </div>
            </div>

            <div className="brutalist-grid">
                <header className="header-block">
                    <div className="header-meta">
                        <span className="sketch-circle">

                            <button
                                onClick={() => setShowGuide(true)}
                                className="brutalist-btn ghost"
                                style={{
                                    fontSize: '0.8rem',
                                    padding: '4px 12px',
                                    textTransform: 'uppercase',
                                    borderWidth: '2px',
                                    fontWeight: 800,
                                    transform: 'rotate(-1deg)',
                                    position: 'relative',
                                    zIndex: 2
                                }}
                            >
                                ? HOW_TO_USE
                            </button>
                        </span>

                        <span style={{ fontFamily: 'var(--font-mono)' }}>
                            {(() => {
                                const d = new Date();
                                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                            })()}
                        </span>
                    </div>
                    <div className="title-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
                        <h1 className="huge-title">
                            Push <br /> Your <br /> Music
                        </h1>

                        {/* Mafalda Sitting on Title */}
                        <div className="doodle-mafalda-container" style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '-25px', zIndex: 10, pointerEvents: 'none' }}>
                            <img src="/mafalda.png" alt="Mafalda" className="doodle-mafalda" />
                        </div>
                    </div>

                </header>

                {error && (
                    <div className="card" style={{ gridColumn: 'span 12', borderColor: 'var(--error)', marginBottom: 'var(--s4)' }}>
                        <span className="brutalist-label" style={{ color: 'var(--error)' }}>! ERROR_DETECTED</span>
                        <p style={{ fontFamily: 'var(--font-mono)' }}>{error}</p>
                        <button onClick={() => setError(null)} className="brutalist-btn ghost" style={{ marginTop: 'var(--s2)' }}>DISMISS</button>
                    </div>
                )}

                <div className="auth-block" style={{ position: 'relative' }}>
                    <div className="card sketch-tape-corner">
                        <div className="paper-clip"></div>



                        <h2 className="brutalist-label">
                            <span className="sketch-highlight">01 / Provider & Connection</span>
                        </h2>


                        <div className="provider-selector">
                            <button
                                onClick={() => setProvider('apple')}
                                className={`provider-btn apple ${provider === 'apple' ? 'active' : ''} ${isAuth && provider === 'apple' ? 'connected' : ''}`}
                                disabled={isLoading}
                            >
                                APPLE MUSIC
                            </button>
                            <div className="provider-btn-wrapper">
                                <button
                                    className="provider-btn spotify disabled"
                                    disabled={true}
                                    title="Spotify temporarily unavailable for new apps"
                                >
                                    SPOTIFY
                                </button>
                                <span className="coming-soon-badge">SOON</span>
                                <div className="provider-tooltip">
                                    Spotify has paused new developer registrations. This will be enabled as soon as they reopen.
                                </div>
                            </div>
                        </div>

                        <div className="auth-content">
                            {!isAuth ? (
                                <>
                                    <p>AUTHORIZE ACCESS TO {providerLabel} LIBRARY.</p>
                                    <button onClick={handleAuthorize} className="brutalist-btn">CONNECT</button>
                                </>
                            ) : (
                                <div className="btn-group">
                                    <button className="brutalist-btn accent">ACTIVE</button>
                                    <button onClick={handleSignOut} className="brutalist-btn ghost">EXIT</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {!results ? (
                    <div className="input-block">
                        <div className="card sketch-tape-corner torn-edge">
                            <div className="red-stamp confidential" style={{ top: '10px', right: '10px' }}>EXPERIMENTAL</div>
                            <div className="paper-clip" style={{ right: 'auto', left: '40px' }}></div>


                            <h2 className="brutalist-label">
                                <span className="sketch-highlight">02 / Tracklist</span>
                            </h2>

                            <div style={{ position: 'relative' }}>
                                <textarea
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={EXAMPLE_SONGS}
                                    className="brutalist-textarea"
                                    disabled={isLoading}
                                />
                                {/* Calvin Doodle */}
                                <div className="doodle-calvin-container" style={{ position: 'absolute', bottom: '15px', right: '15px', zIndex: 10, pointerEvents: 'none' }}>
                                    <img src="/calvin.png" alt="Calvin" className="doodle-calvin" />
                                </div>
                            </div>

                            <div className="options-row">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={includeUncertain}
                                        onChange={(e) => setIncludeUncertain(e.target.checked)}
                                        className="checkbox-input"
                                    />
                                    UNCERTAIN_MATCHES
                                </label>
                                <span className="song-count">
                                    DATA_NODES: <span className="song-count-number">{parsedCount}</span>
                                </span>
                            </div>
                            <p className="input-helper-text">
                                Allows approximate matches (e.g. remixes). Uncheck for strict accuracy.
                            </p>

                            {progress && (
                                <div className="brutalist-progress">
                                    <div className="progress-fill" style={{ width: `${(progress.completed / progress.total) * 100}%` }} />
                                    <span className="progress-label">{progress.message}</span>
                                </div>
                            )}

                            <button
                                onClick={handleImport}
                                disabled={!isAuth || isLoading || !inputText.trim()}
                                className="brutalist-btn btn-full"
                                style={{ marginTop: 'var(--s4)' }}
                            >
                                {isLoading ? 'EXECUTING...' : `SYNC_TO_${provider === 'apple' ? 'ICLOUD' : 'SPOTIFY'}`}
                            </button>

                        </div>
                    </div>
                ) : (
                    <div className="results-block">
                        <div className="card sketch-tape-corner">

                            <h2 className="brutalist-label">
                                <span className="sketch-highlight">03 / Output_Log ({providerLabel})</span>
                            </h2>


                            <div className="stats-row">
                                <div className="stat-item">
                                    <div className="stat-value">{results.imported}</div>
                                    <div className="stat-key">SUCCESS</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-value">{results.summary.matched}</div>
                                    <div className="stat-key">MATCHED</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-value" style={{ color: results.summary.notFound > 0 ? 'red' : 'inherit' }}>{results.summary.notFound}</div>
                                    <div className="stat-key">LOST</div>
                                </div>
                            </div>

                            <div className="track-list">
                                {results.matches.map((item, i) => (
                                    <div key={i} className={`track-row ${item.status === 'not_found' ? 'status--error' : ''}`}>
                                        <div className={`track-status ${item.status === 'matched' ? 'status--success' : ''}`}>
                                            {item.status === 'matched' ? '✓' : item.status === 'uncertain' ? '?' : '✗'}
                                        </div>
                                        <div className="track-info">
                                            <div className="track-original">{item.originalLine}</div>
                                            {item.match && <div className="track-match">{item.match.title} // {item.match.artist}</div>}
                                        </div>
                                        <div className="match-score">
                                            {item.match ? `${Math.round(item.matchScore * 100)}%` : '0%'}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button onClick={handleReset} className="brutalist-btn ghost btn-full" style={{ marginTop: 'var(--s4)' }}>RESTART_V1</button>
                        </div>
                    </div>
                )}
            </div>

            <footer style={{ marginTop: 'var(--s8)', borderTop: 'var(--border)', paddingTop: 'var(--s4)', fontSize: '0.75rem', fontWeight: 700, position: 'relative' }}>
                <div className="doodle doodle-piano" style={{ position: 'absolute', bottom: '60px', left: '50%', transform: 'translateX(-50%)', opacity: 0.2 }}>
                    <svg viewBox="0 0 120 40" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="0" y="0" width="120" height="40" />
                        <path d="M20 0 V40 M40 0 V40 M60 0 V40 M80 0 V40 M100 0 V40" />
                        <rect x="15" y="0" width="10" height="25" fill="currentColor" />
                        <rect x="35" y="0" width="10" height="25" fill="currentColor" />
                        <rect x="65" y="0" width="10" height="25" fill="currentColor" />
                        <rect x="85" y="0" width="10" height="25" fill="currentColor" />
                        <rect x="105" y="0" width="10" height="25" fill="currentColor" />
                    </svg>
                </div>

                [SYSTEM: OK] // [LIB: {provider === 'apple' ? 'MUSICKIT_V3' : 'SPOTIFY_WEB_API'}] // [CORE: REACT_18]
            </footer>


            {showGuide && <HowToModal onClose={() => setShowGuide(false)} />}
        </div>
    );
}

function HowToModal({ onClose }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="brutalist-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">MANUAL.TXT</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <ul className="step-list">
                    <li className="step-item">
                        <span className="step-num">01</span>
                        <div className="step-text">
                            <h4>COPY SOURCE</h4>
                            <p>Get your list of songs from your notes, a napkin, or a loose notebook page. Format: "Song - Artist" works best.</p>
                        </div>
                    </li>
                    <li className="step-item">
                        <span className="step-num">02</span>
                        <div className="step-text">
                            <h4>CONNECT</h4>
                            <p>Authorize your favorite music provider via their secure login. This allows new tracks to be added to your account.</p>
                        </div>
                    </li>
                    <li className="step-item">
                        <span className="step-num">03</span>
                        <div className="step-text">
                            <h4>EXECUTE</h4>
                            <p>Paste the list. Hit SYNC. Watch as the system matches and adds each track to your library.</p>
                        </div>
                    </li>
                </ul>

                <button onClick={onClose} className="brutalist-btn btn-full" style={{ marginTop: '20px' }}>
                    UNDERSTOOD
                </button>
            </div>
        </div>
    );
}

export default App;
